import {
  Card,
  CardContent,
  CardHeader,
  Grid,
} from "@material-ui/core";
import React from 'react';
import { withStyles } from "@material-ui/styles";
import Button from "@material-ui/core/Button";
import CardActions from "@material-ui/core/CardActions";
import TextFieldReadOnly from "../../components/TextFieldReadOnly";
import { connect } from "react-redux";
import CircularProgress from "@material-ui/core/CircularProgress";
import _ from 'lodash';
import Divider from "@material-ui/core/Divider";
import { Event, InsertChart, Power, PowerOff, Speed } from "@material-ui/icons";
import Avatar from "@material-ui/core/Avatar";
import {
  canReconnectICP,
  getICPStateName,
  isDefined,
  renderICPStatusIndicator,
  round,
  safeGet
} from '../../utils';
import moment from 'moment';
import 'moment/locale/es';
import Fade from "@material-ui/core/Fade";
import { MeasuresHistory, ScheduleMeasureDialog } from './components';
import { InfoContainer, LoadingSkeletons, } from "../../components";
import Fab from "@material-ui/core/Fab";
import Container from "@material-ui/core/Container";
import { ContractService } from '../../service';
import NumberFormat from 'react-number-format';
import { openGlobalSnackbar, fetchContract } from "../../actions";
import { compose } from "redux";
import { withTranslation } from "react-i18next";
import Tooltip from "@material-ui/core/Tooltip";
import {i18n, theme} from '../../config';
import Color from "color";
import Settings from "../../settings.yml";

const styles = theme => ({
  mainButton: {
    backgroundColor: theme.palette.primary.light,
    width: "100%",
    marginTop: theme.spacing(2)
  },
  fabButton: {
    margin: theme.spacing(1)
  },
  avatar: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    height: 56,
    width: 56
  },
  icon: {
    height: 32,
    width: 32,
  },
  flexCentered: {
    justifyContent: 'center',
    display: 'flex',
    alignItems: 'center',
  },
  littlePadding: {
    padding: '.5em !important'
  },
  energySection: {
    backgroundColor: Color(theme.palette.primary.main).fade(0.90).rgb().string(),
    width: "100%",
    display: 'flex',
    flexDirection: 'row',
    margin: 10,
  },
  infoCards: {
    padding: theme.spacing(4)
  }
});

const MeterTooltipCountdown = ({countdown, children, meterBusy}) => {
  if (meterBusy) {
    let msg = i18n.t('common:meter_blocked_busy');
    if (countdown > 0) {
      msg = i18n.t('common:meter_blocked_countdown', {count: countdown});
    }
    return (
      <Tooltip title={msg}>
        <div style={{width: "100%", textAlign: 'center'}}>
          {children}
        </div>
      </Tooltip>
    )
  }
  else {
    return <React.Fragment>{children}</React.Fragment>;
  }
}


class MeterDetails extends React.Component {
  constructor(props) {
    super(props);

    this.contractService = new ContractService();

    this.meterBusyInterval = null;

    this.state = {
      meterData: {
        ai: undefined,
        ae: undefined,
        power_import: undefined,
        active_power_import1: undefined,
        active_power_import2: undefined,
        active_power_import3: undefined,
        active_power_export1: undefined,
        active_power_export2: undefined,
        active_power_export3: undefined,
        current_switch_state: undefined,
        previous_switch_state: undefined,
        timestamp: undefined,
        voltage1: undefined,
        voltage2: undefined,
        voltage3: undefined,
      },
      meterBusyCountdown: 0,
      instantLectures: [],
      queryingMeterData: false,
      reconnectingICP: false,
      scheduleMeasureDialogOpen: false,
      refresh: false,
    }
  }

  resetCountdown = () => {
    this.setState({ meterBusyCountdown: 0 });
    clearInterval(this.meterBusyInterval);
  }

  startMeterBusyCountdown = () => {
    this.setState({ meterBusyCountdown: 15 })

    this.meterBusyInterval = setInterval(() => {
      const { meterBusyCountdown } = this.state;
      if (meterBusyCountdown > 0) {
        this.setState({ meterBusyCountdown: meterBusyCountdown - 1 });
      }
      if (meterBusyCountdown === 0) {
        this.resetCountdown();
      }
    }, 1000)
  }

  componentWillUnmount() {
    this.resetCountdown();
    this.contractService.cancelRequests();
  }

  componentDidMount() {
    if(!this.props.contract){
      this.props.fetchContract(this.props.match.params.name)
      .then(
        ([statusCode,]) => {
          if (statusCode === 401) {
            this.props.openGlobalSnackbar(this.props.t('validation:error_fetching_data'), true);
          }
        }
      )
    }
  }

  meterBusy() {
    return this.state.meterBusyCountdown > 0 ? true : this.state.reconnectingICP || this.state.queryingMeterData;
  }

  isDataFetched() {
    return isDefined(this.state.meterData.timestamp);
  }

  setPowerTotal() {
    if (this.isDataFetched()) {
      this.setState({
        meterData: {
          ...this.state.meterData,
          power_import:
            (this.state.meterData.active_power_import1 +
            this.state.meterData.active_power_import2 +
            this.state.meterData.active_power_import3)
            / 1000,
          power_generated: 
            (this.state.meterData.active_power_export1,
            this.state.meterData.active_power_export2,
            this.state.meterData.active_power_export3)
            / 1000
        }
      })
    }
  }

  queryInstantData = async () => {
    this.setState(() => ({ queryingMeterData: true }));
    const { t } = this.props;
    let snackbarResult = {
      isSuccessful: false,
      message: t('validation:meter_connection_error')
    };
    const [statusCode, result] = await this.contractService.meterInstantData(this.props.contract.name, this.props.contract.meter.serial);
    if (statusCode === 200) {
      this.startMeterBusyCountdown();
      snackbarResult.isSuccessful = true;
      snackbarResult.message = t("validation:measure_success");
    }
    else {
      console.debug("Error querying meter data", statusCode, result);
      this.resetCountdown();
      if (statusCode === 429) {
        const responseMessage = result.message || null;
        let max = 2;
        let unit = 'minute';
        let unitQty = 1;
        if (isDefined(responseMessage)){
          [max, , unitQty, unit] = responseMessage.split(' ');
        }
        snackbarResult.message = t('validation:max_requests_exceeded', {
          max: max, every: t(`common:${unit}`, {howMany: unitQty, count: unitQty}).toLowerCase()
        });
      }
    }
    this.setState({
      refresh: true,
      queryingMeterData: false,
      meterData: snackbarResult.isSuccessful ? result : {},
    }, () => {
      snackbarResult.isSuccessful && this.setPowerTotal();
      this.setState({ refresh: false,});
    });
    if (isDefined(snackbarResult.message)) {
      this.props.openGlobalSnackbar(snackbarResult.message, !snackbarResult.isSuccessful);
    }
  };

  reconnectICP = () => {
    const icpState = this.state.meterData.current_switch_state;
    const { t, contract } = this.props;
    if (isDefined(icpState) && canReconnectICP(icpState)) {
      this.setState(() => ({ reconnectingICP: true }));
      let snackbarResult = {
        isSuccessful: false,
        message: t('validation:reconnection_error')
      };
      this.contractService.reconnectICP(contract.name)
      .then(
        ([statusCode, response]) => {
          if (statusCode === 200) {
            this.startMeterBusyCountdown();
            snackbarResult.isSuccessful = true;
            snackbarResult.message = t("validation:reconnection_success");
          }
          else if (statusCode === 429) {
            console.debug("Max retries exceeded");
            snackbarResult.message = t('validation:max_requests_exceeded', {
              max: 2, every: t('common:minute', {count: 2})
            });
          }
          else {
            console.debug("Error reconnecting ICP", response);
          }
          this.setState({ reconnectingICP: false })
          if (isDefined(snackbarResult.message)) {
            this.props.openGlobalSnackbar(snackbarResult.message, !snackbarResult.isSuccessful);
          }
        }
      )
    }
  };

  async handleScheduleMeasureRequest(date) {
    if (date) {
      const date_now = new Date(Date.now() + 5*60000);
      const valid = ((new Date(date)).getTime() - date_now.getTime()) > 0
      const { t } = this.props;
      if (!valid) {
        this.props.openGlobalSnackbar(t('validation:date_must_be_future'), true);
      } else {
        let result = {};
        const scheduledMeasures = await this.contractService.fetchInstantMeterMeasures(
          this.props.contract.name, this.props.contract.meter.serial,'not_executed'
        )
        if (scheduledMeasures.length < 2) {
          const isSuccessful = await this.contractService.scheduleInstantMeasure(
            this.props.contract.name, this.props.contract.meter.serial,
            date.format('YYYY-MM-DD HH:mm:ss')
          );
          if (isSuccessful) {
            result.message = t('validation:measure_schedule_success', {date: date.format('LLL')});
          }
          else {
            result.message = t('validation:unknown_error');
          }
          result.isSuccessful = isSuccessful;
        }
        else {
          result.isSuccessful = false;
          result.message = t('validation:max_scheduled_measures', {how_many: 2});
        }
  
        if (isDefined(result.message)) {
          this.props.openGlobalSnackbar(result.message, !result.isSuccessful);
        }
      }
    }
    this.setState({ scheduleMeasureDialogOpen: false });
  }

  renderMeterReconnectionButton = () => {
    const icpReconnectionEnabled = true;// _.get(Settings, 'icpReconnect', false);
    const { classes, t } = this.props;
    const { reconnectingICP } = this.state;
    const currentState = _.get(this.state.meterData, 'current_switch_state', null);
    if (icpReconnectionEnabled) {
      return (
        <Grid item xs container className={classes.flexCentered}>
          <MeterTooltipCountdown countdown={this.state.meterBusyCountdown}
                                 meterBusy={this.meterBusy()}>
            <Fab
              variant="extended"
              size="medium"
              color="primary"
              aria-label="add"
              disabled={
                !canReconnectICP(currentState) || this.meterBusy()
              }
              className={classes.mainButton}
              onClick={this.reconnectICP}
            >
              {
                reconnectingICP ?
                  <CircularProgress size={20}/>
                  :
                  !isDefined(currentState) || !canReconnectICP(currentState) ?
                    <PowerOff className={classes.icon}/>
                    :
                    <Power className={classes.icon}/>
              }
              {t('reconnect_meter')}
            </Fab>
          </MeterTooltipCountdown>
        </Grid>
      )
    }
  }

  renderMeterData = () => {
    const {contract, classes, t} = this.props;
    const {
      queryingMeterData,
      scheduleMeasureDialogOpen,
    } = this.state;
    return (
      <InfoContainer
        title={t('measuring_device', {name: contract.meter.serial})}
        subtitle={t('common:meter_view_subtitle')}
        titleAvatar={this.renderAvatar()}
      >
        <Divider/>
        <CardContent className={classes.infoCards}>
          <Grid container>
            <Grid item xs={12} component={Card} elevation={0}
                  className={classes.energySection}>
              <Grid item xs={11} container>
                <Grid item xs={12} className={classes.littlePadding}>
                  <CardHeader
                    titleTypographyProps={{
                      style: {
                        fontSize: '1.3em',
                        fontWeight: 600,
                        color: theme.palette.grey[700]
                      }
                    }}
                    title={t('ai')}
                    style={{margin: 0, padding: 0}}/>
                </Grid>
                <Grid item xs={6} className={classes.littlePadding}>
                  <TextFieldReadOnly label={t('current_meter_reading')}>
                    {
                      safeGet(this.state.meterData,
                        'ai', "N/A",
                        value => <NumberFormat displayType="text" suffix={" kWh"}
                                               value={value}
                                               decimalSeparator={","}
                                               thousandSeparator={"."}/>
                      )
                    }
                  </TextFieldReadOnly>
                </Grid >
                <Grid item xs={6} className={classes.littlePadding}>
                  <TextFieldReadOnly label={t('current_power_consumption')}>
                    {
                      safeGet(this.state.meterData,
                        'power_import', "N/A",
                        value => <NumberFormat displayType="text" suffix={" kW"}
                                               value={value}
                                               decimalSeparator={","}
                                               thousandSeparator={"."}/>
                      )
                    }
                  </TextFieldReadOnly>
                </Grid>
              </Grid>
              <Grid item xs={1} className={classes.flexCentered}>
              <svg style={{fill: theme.palette.grey[700]}} x="0px" y="0px" viewBox="0 0 93.533997 110" id="svg30" width="75" height="75"><g id="g24" transform="translate(-3.233,5)"><path d="m 59.632,75.107 v -2.822 c 0,-4.96 2.015,-9.725 5.529,-13.073 4.396,-4.189 6.817,-9.839 6.817,-15.909 0,-12.119 -9.859,-21.978 -21.978,-21.978 -0.827,0 -1.667,0.046 -2.496,0.138 -10.5,1.161 -18.86,10.013 -19.447,20.591 -0.354,6.379 2.066,12.583 6.64,17.02 3.604,3.496 5.671,8.31 5.671,13.208 v 2.824 c 0,2.999 2.439,5.438 5.438,5.438 h 8.387 c 2.999,0.002 5.439,-2.438 5.439,-5.437 z m -4,0 c 0,0.793 -0.646,1.438 -1.438,1.438 h -8.387 c -0.793,0 -1.438,-0.646 -1.438,-1.438 v -2.824 c 0,-5.973 -2.51,-11.833 -6.886,-16.079 -3.741,-3.629 -5.721,-8.706 -5.431,-13.927 0.48,-8.65 7.312,-15.888 15.893,-16.837 0.684,-0.076 1.376,-0.114 2.057,-0.114 9.913,0 17.978,8.064 17.978,17.978 0,4.965 -1.98,9.586 -5.576,13.013 -4.302,4.099 -6.77,9.919 -6.77,15.969 v 2.821 z" id="path2"/><path d="M 56.509,82.521 H 43.491 c -0.829,0 -1.5,0.671 -1.5,1.5 0,0.829 0.671,1.5 1.5,1.5 H 56.51 c 0.829,0 1.5,-0.671 1.5,-1.5 0,-0.829 -0.672,-1.5 -1.501,-1.5 z" id="path4"/><path d="m 58.009,88.761 c 0,-0.829 -0.671,-1.5 -1.5,-1.5 H 43.491 c -0.829,0 -1.5,0.671 -1.5,1.5 0,0.829 0.671,1.5 1.5,1.5 H 56.51 c 0.828,0 1.499,-0.672 1.499,-1.5 z" id="path6"/><path d="m 14.733,43.267 h 8.643 c 0.829,0 1.5,-0.671 1.5,-1.5 0,-0.829 -0.671,-1.5 -1.5,-1.5 h -8.643 c -0.829,0 -1.5,0.671 -1.5,1.5 0,0.829 0.671,1.5 1.5,1.5 z" id="path8"/><path d="m 86.767,41.767 c 0,-0.829 -0.671,-1.5 -1.5,-1.5 h -8.643 c -0.829,0 -1.5,0.671 -1.5,1.5 0,0.829 0.671,1.5 1.5,1.5 h 8.643 c 0.829,0 1.5,-0.671 1.5,-1.5 z" id="path10"/><path d="m 48.5,6.5 v 8.643 c 0,0.829 0.671,1.5 1.5,1.5 0.829,0 1.5,-0.671 1.5,-1.5 V 6.5 C 51.5,5.671 50.829,5 50,5 49.171,5 48.5,5.671 48.5,6.5 Z" id="path12"/><path d="m 73.877,15.769 -6.111,6.111 c -0.586,0.585 -0.586,1.536 0,2.121 0.293,0.293 0.677,0.439 1.061,0.439 0.384,0 0.768,-0.146 1.061,-0.439 l 6.111,-6.111 c 0.586,-0.585 0.586,-1.536 0,-2.121 -0.587,-0.586 -1.536,-0.586 -2.122,0 z" id="path14"/><path d="m 32.234,59.533 c -0.586,-0.586 -1.535,-0.586 -2.121,0 l -6.111,6.111 c -0.586,0.585 -0.586,1.535 0,2.121 0.293,0.293 0.677,0.439 1.061,0.439 0.384,0 0.768,-0.146 1.061,-0.439 l 6.111,-6.111 c 0.585,-0.585 0.585,-1.535 -10e-4,-2.121 z" id="path16"/><path d="m 30.113,24.001 c 0.293,0.293 0.677,0.439 1.061,0.439 0.384,0 0.768,-0.146 1.061,-0.439 0.586,-0.585 0.586,-1.536 0,-2.121 l -6.111,-6.111 c -0.586,-0.586 -1.535,-0.586 -2.121,0 -0.586,0.585 -0.586,1.536 0,2.121 z" id="path18"/><path d="m 73.877,67.765 c 0.293,0.293 0.677,0.439 1.061,0.439 0.384,0 0.768,-0.146 1.061,-0.439 0.586,-0.586 0.586,-1.536 0,-2.121 l -6.111,-6.111 c -0.586,-0.586 -1.535,-0.586 -2.121,0 -0.586,0.586 -0.586,1.536 0,2.121 z" id="path20"/><path d="m 54.754,93.5 c 0,-0.829 -0.671,-1.5 -1.5,-1.5 h -6.509 c -0.829,0 -1.5,0.671 -1.5,1.5 0,0.829 0.671,1.5 1.5,1.5 h 6.509 c 0.829,0 1.5,-0.671 1.5,-1.5 z" id="path22"/></g></svg>
            </Grid>
            </Grid>
            <Grid item xs={12} component={Card} elevation={0}
                  className={classes.energySection}>
              <Grid item xs={11} container>
                <Grid item xs={12} className={classes.littlePadding}>
                  <CardHeader
                    titleTypographyProps={{
                      style: {
                        fontSize: '1.3em',
                        fontWeight: 600,
                        color: theme.palette.grey[700]
                      }
                    }}
                    title={t('ae')}
                    style={{margin: 0, padding: 0}}/>
                </Grid>
                <Grid item xs={6} className={classes.littlePadding}>
                  <TextFieldReadOnly label={t('current_meter_reading_generation')}>
                    {
                      safeGet(this.state.meterData,
                        'ae', "N/A",
                        value => <NumberFormat displayType="text" suffix={" kWh"}
                                               value={value}
                                               decimalSeparator={","}
                                               thousandSeparator={"."}/>
                      )
                    }
                  </TextFieldReadOnly>
                </Grid>
                <Grid item xs={6} className={classes.littlePadding}>
                  <TextFieldReadOnly label={t('current_power_generated')}>
                    {
                      safeGet(this.state.meterData,
                        'power_generated', "N/A",
                        value => <NumberFormat displayType="text" suffix={" kW"}
                                               value={value}
                                               decimalSeparator={","}
                                               thousandSeparator={"."}/>
                      )
                    }
                  </TextFieldReadOnly>
                </Grid>
              </Grid>
              <Grid item xs={1} className={classes.flexCentered}>
                <svg xmlns="http://www.w3.org/2000/svg" version="1.1" id="Layer_1" x="0px" y="0px" style={{enableBackground: "new 0 0 480 480", fill: theme.palette.grey[700]}} viewBox="0 0 481 480" width="65" height="65"><g id="Outline"><path className="st0" d="M464,176c0,12.8-1.8,25.5-5.3,37.8l15.3,4.5c4-13.7,6-27.9,6-42.2c0-71.5-48.6-132.5-118.2-148.2l-3.5,15.6 C419.5,57.2,464,113,464,176z"/><path className="st0" d="M182,218.2l15.4-4.5c-3.6-12.3-5.4-25-5.4-37.8c0-60.2,40.4-114,98.2-130.7l-4.4-15.4 C221.1,48.6,176,108.7,176,176C176,190.3,178,204.5,182,218.2z"/><path className="st0" d="M432,176c0,5-0.4,9.9-1.1,14.9l15.8,2.3c0.8-5.7,1.2-11.4,1.2-17.2c0-52.7-35.2-100-85.7-115l-4.6,15.3 C401.7,89.6,431.8,130.1,432,176z"/><path className="st0" d="M289.3,79.4l-6-14.8c-57.5,23.1-88,86.3-70.3,145.7l15.3-4.6C213,154.2,239.4,99.5,289.3,79.4z"/><path className="st0" d="M8,432h128v32h-24v16h96v-16h-24v-32h80c3.4,0,6.5-2.2,7.6-5.5l27.9-83.7L296.2,464H280v16h96v-16h-16.2 L356,325.2c31.2-5.8,59.8-21.2,81.8-44.1L426.2,270c-19,19.9-43.7,33.5-70.6,39l-0.4-16.2c21.5-4.9,41.1-15.7,56.8-31.1l-11.2-11.4 c-12.8,12.6-28.6,21.6-46,26.2l-1.8-65.9L455.7,270c2.4,1.3,5.1,2,7.8,2c9.2-0.1,16.5-7.7,16.4-16.8c0-3.4-1.1-6.7-3.1-9.5 c-3.8-5.1-7.6-9.8-11.5-14.1c-0.2-0.2-0.4-0.5-0.6-0.7c-8-8.8-16.7-16.9-26.1-24.1c-23.4-18-50.6-30.6-79.6-36.6 c-1.6-9.1-7.1-17.1-15-21.9V16.1c-0.1-9-7.4-16.2-16.4-16.1c-6.6,0-12.5,3.8-15.2,9.8c-2.9,6.7-5.5,13.6-7.6,20.6 c-3.6,11.4-6.1,23-7.4,34.9c-3.5,29.2-0.3,58.8,9.2,86.5c-6.8,6-10.6,14.7-10.7,23.7c0,0.2,0.1,0.4,0.1,0.6L213.3,224H72 c-3.4,0-6.5,2.2-7.6,5.5l-64,192c-1.4,4.2,0.9,8.7,5.1,10.1C6.3,431.9,7.1,432,8,432z M343.5,175.6c-0.1,8.7-7.3,15.7-16,15.6 c-8.7-0.1-15.7-7.3-15.6-16c0.1-8.6,7.2-15.6,15.8-15.6c8.7,0,15.8,7.1,15.8,15.8C343.6,175.4,343.6,175.5,343.5,175.6z M176,416 h-28.9l10.7-32h47.1l-10.7,32H176z M243.1,320l10.7-32h47.1l-10.7,32H243.1z M284.9,336l-10.7,32h-47.1l10.7-32H284.9z M226.2,320 h-47.1l10.7-32h47.1L226.2,320z M220.9,336l-10.7,32h-47.1l10.7-32H220.9z M221.8,384h47.1l-10.7,32h-47.1L221.8,384z M316.8,295.5 c3.7,0.3,7.4,0.5,11.2,0.5s7.5-0.2,11.2-0.6l0.4,16c-7.8,0.7-15.5,0.7-23.3,0L316.8,295.5z M320.5,279.7l15.1-45.2 c1.4-4.2-0.9-8.7-5.1-10.1c-0.8-0.3-1.7-0.4-2.5-0.4h-9.3l0.5-17.5l0.2-0.3c5.7,1.8,11.8,1.7,17.4-0.2l2,73.3 C332.8,280,326.6,280.1,320.5,279.7L320.5,279.7z M302.7,224h-1c0.4-0.3,0.7-0.6,1-0.9V224z M316.9,240l-10.7,32h-47.1l10.7-32 H316.9z M242.2,272h-47.1l10.7-32h47.1L242.2,272z M178.2,272h-47.1l10.7-32h47.1L178.2,272z M172.9,288l-10.7,32h-47.1l10.7-32 H172.9z M98.2,320H51.1l10.7-32h47.1L98.2,320z M92.9,336l-10.7,32H35.1l10.7-32H92.9z M109.8,336h47.1l-10.7,32H99.1L109.8,336z M140.9,384l-10.7,32H83.1l10.7-32H140.9z M168,464h-16v-32h16V464z M312.2,464l3.7-136.5c4,0.3,8,0.5,12.1,0.5s8.1-0.2,12.1-0.5 l3.7,136.5H312.2z M452.3,240.9c0.2,0.2,0.4,0.5,0.6,0.7c3.9,4.3,7.6,8.9,11.1,13.6l-0.4,0.8l-109.2-63.1c1.4-2.1,2.4-4.4,3.2-6.8 c25.9,5.7,50.2,17,71.2,33.2C437.3,225.8,445.1,233,452.3,240.9z M313.3,67.1c1.3-10.9,3.5-21.6,6.8-32.1c2-6.4,4.3-12.8,7-19 l0.9,0v127.5h-0.2c-2.3,0-4.6,0.3-6.8,0.8C312.9,119.5,310.2,93.1,313.3,67.1z M300.7,192c1.3,2.2,2.8,4.3,4.6,6.1 c-8.8,9.7-18.8,18.4-29.5,25.9h-30.5L300.7,192z M77.8,240h47.1l-10.7,32H67.1L77.8,240z M29.8,384h47.1l-10.7,32H19.1L29.8,384z"/><path className="st0" d="M160,64c30.9,0,56-25.1,56-56c0-4.4-3.6-8-8-8c-30.9,0-56,25.1-56,56C152,60.4,155.6,64,160,64z M199,17 c-3.5,14.9-15.1,26.5-30,30C172.5,32.1,184.1,20.5,199,17z"/><path className="st0" d="M424,328c-4.4,0-8,3.6-8,8c0,30.9,25.1,56,56,56c4.4,0,8-3.6,8-8C480,353.1,454.9,328,424,328z M433,345 c14.9,3.5,26.5,15.1,30,30C448.1,371.5,436.5,359.9,433,345z"/><path className="st0" d="M104,160c4.4,0,8-3.6,8-8c0-30.9-25.1-56-56-56c-4.4,0-8,3.6-8,8C48,134.9,73.1,160,104,160z M95,143 c-14.9-3.5-26.5-15.1-30-30C79.9,116.5,91.5,128.1,95,143z"/></g></svg>
              </Grid>
            </Grid>

            <Grid item container xs={12} sm={6}>
              <Grid item xs>
                <TextFieldReadOnly label={t('icp_state')}>
                  {renderICPStatusIndicator(_.get(this.state.meterData, 'current_switch_state'))}
                  {
                    safeGet(this.state.meterData, 'current_switch_state', "N/A",
                      state => getICPStateName(
                        safeGet(this.state.meterData, 'previous_switch_state'),
                        state
                      )
                    )
                  }
                </TextFieldReadOnly>
              </Grid>
              {
                this.renderMeterReconnectionButton()
              }
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldReadOnly label={t('measure_date')}>
                {
                  safeGet(this.state.meterData,
                    'timestamp', "N/A",
                    timestamp => moment(timestamp, moment.ISO_8601).format('LL, [a las] HH:mm:ss')
                  )
                }
              </TextFieldReadOnly>
            </Grid>
            <Divider/>
            <Grid item xs={12} container>
              <Grid item xs>
                <CardActions>
                  <MeterTooltipCountdown countdown={this.state.meterBusyCountdown} meterBusy={this.meterBusy()}>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={this.meterBusy()}
                      startIcon={queryingMeterData ?
                        <CircularProgress size={20}/> : <Speed/>}
                      className={classes.mainButton}
                      onClick={this.queryInstantData}
                    >
                      {t('common:instant_measure')}
                    </Button>
                  </MeterTooltipCountdown>
                </CardActions>
              </Grid>
              <Grid item xs>
                <CardActions>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Event/>}
                    className={classes.mainButton}
                    onClick={() => this.setState({
                      scheduleMeasureDialogOpen: true
                    })}
                  >
                    {t('schedule_measure')}
                  </Button>
                </CardActions>
                <ScheduleMeasureDialog
                  open={scheduleMeasureDialogOpen}
                  onClose={(date) => this.handleScheduleMeasureRequest(date)}/>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </InfoContainer>
    );
  }

  renderAvatar() {
    const {classes} = this.props;
    return (
      <Grid item xs={2} className={classes.flexCentered}>
        <Avatar className={classes.avatar}>
          <InsertChart className={classes.icon}/>
        </Avatar>
      </Grid>
    )
  }

  renderContent() {
    const {contract} = this.props;
    if (contract) {
      return (
        <React.Fragment>
          {this.renderMeterData()}
          <MeasuresHistory contract={contract} refresh={this.state.refresh}/>
        </React.Fragment>
      )
    }
    else {
      return <LoadingSkeletons fullHeight/>;
    }
  }

  render() {
    return (
      <Fade in={true}>
        <Container maxWidth={"lg"}>
          {this.renderContent()}
        </Container>
      </Fade>
    )
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    contract: _.get(state, `contracts.items.${ownProps.match.params.name}`, null),
  };
};

const mapDispatchToProps = {
  fetchContract,
  openGlobalSnackbar: openGlobalSnackbar
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
  withTranslation(['common', 'validation'])
)(MeterDetails);


